import { Alert, Box, Snackbar } from '@mui/material';
import { Header } from 'components/home';
import { StepperHorizontal } from 'components/shared/stepper_horizontal';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import React, { useState, useEffect } from 'react';
import Step1 from './Step1';
import Step2 from './Step2';
import useCifaStore from 'stores/cifa/cifa.store';
import { ROUTES } from 'routes/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import CifaAPI from 'api/cifa/cifa.api';
import { CifaStatusEnum } from 'enum/cifaStatus.enum';

interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

type AnswersType = {
  secoes: SecaoAquisicao[];
  institution_uuid: string;
};

const SchoolFeedingAcquisitions: React.FC = () => {
  const steps = ['Alimentação Escolar Aquisições', 'Confirmação dos Dados'];
  const [activeStep, setActiveStep] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { cifa, setPartialForm } = useCifaStore();
  const [answers, setAnswers] = useState<AnswersType>({
    secoes: [
      {
        titulo: 'Recursos do PNAE/Federal',
        produtosCnpj: cifa.aquisicoes_pnae_federal_detalhamento?.cnpj?.map(p => ({
          id: Math.random().toString(36).substr(2, 9),
          nomeRazao: p.nomeRazao,
          documento: p.cnpj ?? '',
          valor: p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          valorNumerico: p.valor
        })) || [],
        produtosCpf: cifa.aquisicoes_pnae_federal_detalhamento?.cpf?.map(p => ({
          id: Math.random().toString(36).substr(2, 9),
          nomeRazao: p.nomeRazao,
          documento: p.cpf ?? '',
          valor: p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          valorNumerico: p.valor
        })) || [],
      },
      {
        titulo: 'Recursos do Governo do Estado',
        produtosCnpj: cifa.aquisicoes_governo_estado_detalhamento?.cnpj?.map(p => ({
          id: Math.random().toString(36).substr(2, 9),
          nomeRazao: p.nomeRazao,
          documento: p.cnpj ?? '',
          valor: p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          valorNumerico: p.valor
        })) || [],
        produtosCpf: cifa.aquisicoes_governo_estado_detalhamento?.cpf?.map(p => ({
          id: Math.random().toString(36).substr(2, 9),
          nomeRazao: p.nomeRazao,
          documento: p.cpf ?? '',
          valor: p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          valorNumerico: p.valor
        })) || [],
      },
      {
        titulo: 'Recursos da Prefeitura',
        produtosCnpj: cifa.aquisicoes_prefeitura_detalhamento?.cnpj?.map(p => ({
          id: Math.random().toString(36).substr(2, 9),
          nomeRazao: p.nomeRazao,
          documento: p.cnpj ?? '',
          valor: p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          valorNumerico: p.valor
        })) || [],
        produtosCpf: cifa.aquisicoes_prefeitura_detalhamento?.cpf?.map(p => ({
          id: Math.random().toString(36).substr(2, 9),
          nomeRazao: p.nomeRazao,
          documento: p.cpf ?? '',
          valor: p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          valorNumerico: p.valor
        })) || [],
      }
    ],
    institution_uuid: cifa.uuid || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const match = location.pathname.match(/cifa-(\d{4})/);
  const anoReferencia = match ? Number(match[1]) : 2026;

  useEffect(() => {
    if (!cifa.uuid) {
      navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
    }
  }, [cifa.uuid, navigate, anoReferencia]);

  const handleNext = (newAnswers: Partial<AnswersType>) => {
    const updatedAnswers = {
      ...answers,
      ...newAnswers,
      institution_uuid: cifa.uuid || answers.institution_uuid
    };

    setAnswers(updatedAnswers);

    setPartialForm('aquisicoes_pnae_federal_detalhamento', convertToApiFormat(updatedAnswers.secoes[0]));
    setPartialForm('aquisicoes_governo_estado_detalhamento', convertToApiFormat(updatedAnswers.secoes[1]));
    setPartialForm('aquisicoes_prefeitura_detalhamento', convertToApiFormat(updatedAnswers.secoes[2]));

    setActiveStep(prevStep => prevStep + 1);
  };

  const convertToApiFormat = (secao: SecaoAquisicao) => ({
    cnpj: secao.produtosCnpj.map(p => ({
      nomeRazao: p.nomeRazao,
      cnpj: p.documento.replace(/\D/g, ''),
      valor: p.valorNumerico
    })),
    cpf: secao.produtosCpf.map(p => ({
      nomeRazao: p.nomeRazao,
      cpf: p.documento.replace(/\D/g, ''),
      valor: p.valorNumerico
    }))
  });

  const handleBack = () => setActiveStep(prevStep => prevStep - 1);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const payload = {
        aquisicoes_pnae_federal_detalhamento: convertToApiFormat(answers.secoes[0]),
        aquisicoes_governo_estado_detalhamento: convertToApiFormat(answers.secoes[1]),
        aquisicoes_prefeitura_detalhamento: convertToApiFormat(answers.secoes[2])
      };


      await CifaAPI.updateSchoolFeedingAcquisitions(cifa.uuid!, payload);

      setPartialForm('aquisicoes_pnae_federal_detalhamento', payload.aquisicoes_pnae_federal_detalhamento);
      setPartialForm('aquisicoes_governo_estado_detalhamento', payload.aquisicoes_governo_estado_detalhamento);
      setPartialForm('aquisicoes_prefeitura_detalhamento', payload.aquisicoes_prefeitura_detalhamento);
      setPartialForm('status_aba_4', CifaStatusEnum.APPROVED);

      setSnackbar({
        open: true,
        message: 'Dados salvos com sucesso!',
        severity: 'success'
      });

      setTimeout(() => navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string), 1500);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar os dados. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title={`CIFA - Alimentação Escolar Aquisições - ${anoReferencia}`} showBackButton={true} />
      <Box width='97%' display='flex' flexDirection='column' justifyContent='center' alignItems='center' margin='2rem'>
        <StepperHorizontal activeStep={activeStep} steps={steps} maxWidth='1152px' />

        {activeStep === 0 && (
          <Step1
            initialData={answers.secoes}
            onNext={(secoes) => handleNext({ secoes })}
          />
        )}

        {activeStep === 1 && (
          <Step2
            answers={answers}
            onBack={handleBack}
            onSaveSuccess={handleSubmit}
          />
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MenuProvider>
  );
};

export default SchoolFeedingAcquisitions;